import { BlueBirdTheme, Button, Typography } from "@kitbird/core";
import Status, { StatusEnums } from "../../../components/Status";
import { ColumnsI } from "../../../components/Table/TableProps";
import { IGetTrxData, IGetTrxHistoryData } from "../../../services/trxService";
import { isHtmlString, toRupiah, toStringDate } from "../../../utils/common";
import {
  ApprovalModalType, PageType, TabTypes,
} from "./enums";
import { DescriptionCol } from "./components/styles";
import { ReactComponent as ViewIcon } from "../../../assets/icons/show_icon.svg";
import { ReactComponent as TrashIcon } from "../../../assets/icons/trash.svg";
import { ReactComponent as CheckIcon } from "../../../assets/icons/check_mark.svg";
import { ReactComponent as CrossIcon } from "../../../assets/icons/cross_mark.svg";
import { CommonTrxTypes } from "../../../enums/common";
import { isIGetTrxHistoryData } from "./hooks/utils";

const {
  danger, gray, primary, success,
} = BlueBirdTheme.colors;

const renderTrxStatus = (item?: "OPEN" | "CLOSE" | "CANCEL" | "TRANSFER") => {
  const val = item ? {
    OPEN: { text: "Diproses", stat: StatusEnums.PRIMARY },
    CLOSE: { text: "Selesai", stat: StatusEnums.SUCCESS },
    CANCEL: { text: "Batal", stat: StatusEnums.DANGER },
    TRANSFER: { text: "Proses transfer", stat: StatusEnums.RATING },
  }[item] : { text: "", stat: StatusEnums.INACTIVE };

  return (
    (val && val.text) ? <Status content={val.text} status={val.stat} /> : <></>
  );
};

const renderExtendStatus = (item: any) => (
  <div style={{ textAlign: "center" }}>
    {(item && item !== "0") ? item : "-"}
  </div>
);

const columns = (
  setPageType: React.Dispatch<React.SetStateAction<PageType>>,
  setOpen: React.Dispatch<React.SetStateAction<ApprovalModalType | false>>,
  setDetailRow: React.Dispatch<React.SetStateAction<IGetTrxData | undefined>>,
  pageType: PageType | false,
  tab: TabTypes | false,
  isCreator?: boolean,
  isApprover?: boolean,
  isSKA?: boolean,
): ColumnsI => [{
  title: "Status bon",
  dataIndex: "status_trx",
  key: "status_trx",
  width: "150px",
  render: renderTrxStatus,
},
{
  title: "Perpanjangan bon ke",
  dataIndex: "extend_bm",
  key: "extend_bm",
  width: "150px",
  render: renderExtendStatus,
},
{
  title: "No. pengajuan bon",
  dataIndex: "trx_id",
  key: "trx_id",
  width: "150px",
},
{
  title: "Tanggal pengajuan",
  dataIndex: "trx_date",
  key: "trx_date",
  sort: true,
  width: "160px",
  render: (item) => toStringDate(item),
},
isSKA ? {
  title: "Tanggal transfer",
  dataIndex: "transfer_date",
  key: "transfer_date",
  width: "160px",
  render: (item) => toStringDate(item) || "-",
} : undefined,
{
  title: "Total bon & tipe transaksi",
  dataIndex: "trx_type",
  key: "trx_type",
  width: "175px",
  render: (item, row) => (
    <>
      <Typography level={6} fontWeight="medium" color={gray.subtitle}>
        {toRupiah(row.amount || 0)}
      </Typography>
      <br />
      <Typography level={7} color={gray.bodyText}>
        {isIGetTrxHistoryData(row as IGetTrxHistoryData)
          ? row.trx_type_description
          : item.description}
      </Typography>
    </>
  ),
},
{
  title: "Deskripsi",
  dataIndex: "trx_text",
  key: "trx_text",
  width: "225px",
  render: (item) => (isHtmlString(item)
    ? (<DescriptionCol
      dangerouslySetInnerHTML={{ __html: item }}
      $isEllipsis
    />)
    : (<DescriptionCol $isEllipsis>
      {item}
    </DescriptionCol>)
  ),
},
{
  title: "Pemohon & NIP",
  dataIndex: "employee_nip",
  key: "employee_nip",
  width: "125px",
  render: (item, row) => (
    <>
      <Typography level={6} fontWeight="medium" color={gray.subtitle}>
        {row.employee_name}
      </Typography>
      <br />
      <Typography level={7} color={gray.bodyText}>
        {item}
      </Typography>
    </>
  ),
},
{
  title: "Departemen & jabatan",
  dataIndex: "department",
  key: "department",
  width: "155px",
  render: (item, row) => (
    <>
      <Typography level={6} fontWeight="medium" color={gray.subtitle}>
        {item.department}
      </Typography>
      <br />
      <Typography level={7} color={gray.bodyText}>
        {row.job_title}
      </Typography>
    </>
  ),
},
{
  title: "Segmen",
  dataIndex: "segment",
  key: "segment",
  width: "125px",
  render: (item, row) => (isIGetTrxHistoryData(row as IGetTrxHistoryData)
    ? row.code_description
    : item.code_description
  ),
},
{
  title: "Wilayah & pool",
  dataIndex: "area_name",
  key: "area_name",
  width: "125px",
  render: (item, row) => (
    <>
      <Typography level={6} fontWeight="medium" color={gray.subtitle}>
        {item}
      </Typography>
      <br />
      <Typography level={7} color={gray.bodyText}>
        {row.pool_id}
      </Typography>
    </>
  ),
},
{
  title: "Perusahaan & relasi",
  dataIndex: "company_code",
  key: "company_code",
  width: "145px",
  render: (item, row) => (
    <>
      <Typography level={6} fontWeight="medium" color={gray.subtitle}>
        {item}
      </Typography>
      <br />
      {row.company_relation && (
        <Typography level={7} color={gray.bodyText}>
          {row.company_relation}
        </Typography>
      )}
    </>
  ),
},
{
  title: "Akun GL",
  dataIndex: "trx_code",
  key: "trx_code",
  width: "175px",
  render: (item, row) => (
    <>
      <Typography level={6} fontWeight="medium" color={gray.subtitle}>
        {item.trx_code} • {CommonTrxTypes.find(
          (type) => type.value === row.sap_shkzg,
        )?.label}
      </Typography>
      <br />
      <Typography level={7} color={gray.bodyText}>
        {item.trx_desc}
      </Typography>
    </>
  ),
},
{
  title: "Rekening tujuan",
  dataIndex: "bank_account_name",
  key: "bank_account_name",
  width: "125px",
  render: (item, row) => (
    <>
      <Typography level={6} fontWeight="medium" color={gray.subtitle}>
        {item}
      </Typography>
      <br />
      <Typography level={7} color={gray.bodyText}>
        {row.bank_account}
      </Typography>
    </>
  ),
},
{
  title: "Estimasi tanggal kirim",
  dataIndex: "plan_postdate",
  key: "plan_postdate",
  sort: true,
  width: "175px",
  render: (item) => toStringDate(item),
},
{
  title: "Lampiran",
  dataIndex: "trx_files",
  key: "trx_files",
  width: "200px",
  render: (item) => (
    <div className="ellipsis-text" style={{ width: 200 }}>
      {
        !!item.length && `${item[0].file_name}${item.length > 1 ? ` +${item.length - 1}` : ""}`
      }{
        !!item.file_text && `${item.file_text}${item.file_count > 1 ? ` +${item.file_count - 1}` : ""}`
      }{
        (!item.length && !item.file_text) && "-"
      }
    </div>
  ),
},
{
  title: "Action",
  dataIndex: "trx_id",
  key: "action",
  width: "100px",
  sticky: "right",
  render: (item, row) => (
    pageType === PageType.LIST ? (<div style={{ display: "flex", gap: 8 }}>
      {isCreator && <>
        <Button
          style={{ padding: "8px 10px" }}
          backgroundColor={primary[300]}
          onClick={(e) => {
            e.stopPropagation();
            setDetailRow(row as IGetTrxData);
            setPageType(PageType.DETAIL);
          }}
        >
          <ViewIcon width={16} height={16} />
        </Button>
        {(
          !!row.is_approval_progress
          && row.status_trx !== "CANCEL"
          && row.status_trx !== "CLOSE"
        )
          && <Button
            style={{ padding: "8px 10px" }}
            backgroundColor={danger[400]}
            onClick={(e) => {
              e.stopPropagation();
              setDetailRow(row as IGetTrxData);
              setOpen(ApprovalModalType.DELETE);
            }}
          >
            <TrashIcon width={16} height={16} />
          </Button>
        }
      </>}
      {(isApprover && tab === TabTypes.OPEN) && <>
        <Button
          style={{ padding: "8px 10px" }}
          backgroundColor={success[400]}
          onClick={(e) => {
            e.stopPropagation();
            setDetailRow(row as IGetTrxData);
            setOpen(ApprovalModalType.APPROVE);
          }}
        >
          <CheckIcon width={16} height={16} />
        </Button>
        <Button
          style={{ padding: "8px 10px" }}
          backgroundColor={danger[400]}
          onClick={(e) => {
            e.stopPropagation();
            setDetailRow(row as IGetTrxData);
            setOpen(ApprovalModalType.REJECT);
          }}
        >
          <CrossIcon width={16} height={16} />
        </Button>
      </>}
      {(isApprover && tab !== TabTypes.OPEN) && <>
        <Button
          style={{ padding: "8px 10px" }}
          backgroundColor={primary[300]}
          onClick={(e) => {
            e.stopPropagation();
            setDetailRow(row as IGetTrxData);
            setPageType(PageType.DETAIL);
          }}
        >
          <ViewIcon width={16} height={16} />
        </Button>
      </>}
    </div>
    ) : (
      <Button
        style={{ padding: "8px 10px" }}
        backgroundColor={primary[300]}
        onClick={(e) => {
          e.stopPropagation();
          setDetailRow(row as IGetTrxData);
          setPageType(PageType.HISTORY_DETAIL);
        }}
      >
        <ViewIcon width={16} height={16} />
      </Button>
    )
  ),
}];

export default columns;
